<template>
  <div class="relative">
    <button
      type="button"
      @click="open = !open"
      :disabled="!store.stats"
      class="h-7 inline-flex items-center gap-1.5 px-2.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"/>
      </svg>
      Export
    </button>

    <div v-if="open" @click="open = false" class="fixed inset-0 z-20"></div>
    <div v-if="open" class="absolute right-0 top-full mt-1.5 z-30 w-52 p-1.5 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg">
      <button v-for="opt in OPTIONS" :key="opt.value" type="button" @click="run(opt.value)"
        class="w-full flex flex-col items-start px-2.5 py-1.5 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <span class="text-xs font-semibold text-gray-700 dark:text-gray-200">{{ opt.label }}</span>
        <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ opt.hint }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useTaskStatsStore } from 'stores/taskStats.js'
import { buildCsv, downloadCsv, downloadJson } from 'utils/csv.js'

const store = useTaskStatsStore()
const open = ref(false)

const OPTIONS = [
  { value: 'csv', label: 'CSV', hint: 'Every table, one file' },
  { value: 'json', label: 'JSON', hint: 'The raw figures' },
]

// Exports come straight off the store, never a second request. The rule is
// that you export exactly what you are looking at: same filters, same numbers,
// same instant. A separate endpoint could drift from the screen.
function run(format) {
  open.value = false
  const stats = store.stats
  if (!stats) return

  const stamp = `${stats.range.start}_to_${stats.range.end}`
  if (format === 'json') return downloadJson(`task-stats-${stamp}.json`, stats)
  downloadCsv(`task-stats-${stamp}.csv`, csvFor(stats))
}

function csvFor(stats) {
  const f = store.filters
  return buildCsv({
    meta: [
      ['Generated', new Date().toISOString()],
      ['Range', `${stats.range.start} to ${stats.range.end}`],
      ['Priorities', f.priorities.join(' ') || 'all'],
      ['Colours', f.colors.join(' ') || 'all'],
      ['Task type', f.taskType],
      ['Cancelled included', f.includeCanceled ? 'yes' : 'no'],
    ],
    sections: [
      {
        name: 'Headline figures',
        headers: ['Metric', 'Value', 'Previous period'],
        rows: Object.entries(stats.kpis).map(([key, m]) => [key, m.value ?? '', m.previous ?? '']),
      },
      {
        name: 'Created vs completed',
        headers: ['Date', 'Created', 'Completed'],
        rows: stats.over_time.map(d => [d.date, d.created, d.completed]),
      },
      {
        name: 'Open by status',
        headers: ['Status', 'Count'],
        rows: stats.by_status.map(d => [d.status, d.count]),
      },
      {
        name: 'By priority',
        headers: ['Priority', 'Completed', 'Open', 'Overdue'],
        rows: stats.by_priority.map(d => [d.priority, d.completed, d.open, d.overdue]),
      },
      {
        name: 'Cycle time',
        headers: ['Bucket', 'Count'],
        rows: stats.cycle_time.map(d => [d.bucket, d.count]),
      },
      {
        name: 'Overdue aging',
        headers: ['Bucket', 'Count'],
        rows: stats.overdue_aging.map(d => [d.bucket, d.count]),
      },
      {
        name: 'Longest open',
        headers: ['Title', 'Age (days)', 'Priority', 'Status', 'Due', 'Overdue'],
        rows: stats.longest_open.map(r => [r.title, r.age_days, r.priority, r.status, r.due_at || '', r.is_overdue ? 'yes' : 'no']),
      },
    ],
  })
}
</script>
